import { useCallback } from "react";

// Third-party
import { useQueryClient } from "@tanstack/react-query";

// MUI
import { Box, Grid, Stack, Typography } from "@mui/material";

// Hooks
import { usePaginatedResource } from "@/Common/hooks/Pagination";

// Context
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// API
import { getTradeInPayOffs } from "@DataPages/api/financeManagement/tradeInPayOffs/queries.ts";

// Components
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import TradeInPayoffsTable from "./TradeInPayoffsTable";

// Helpers
import { buildGetNextPageParam, DEFAULT_CURSOR, PAGE_SIZE } from "@/Common/hooks/Pagination/helpers";
import { and } from "@/Common/helpers/sonar";

// Types
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { IPageParam } from "@/Common/api/types/pagination";
import { IPayoffSubscriptionResponse } from "@/DataPages/types/financeManagement/model/trade-in-payoff";

// Assets
import emptyTable from "@/assets/images/commons/empty_table.png";

type TradeInPayoffsTabProps = {
	selected: IPeriodTreeSelection;
	locationId: string;
	isTabSelected: boolean;
	hasPermission: boolean;
};

export default function TradeInPayoffsBody(props: Readonly<TradeInPayoffsTabProps>) {
	const { selected, locationId, isTabSelected, hasPermission } = props;

	const queryClient = useQueryClient();
	const { getSubscription } = useSubscriptions();
	const payoffsSubscription = getSubscription(["payoffs", selected?._id]);
	const {
		data: tradeInPayoffs,
		isFetching,
		hasNextPage,
		status,
	} = usePaginatedResource({
		queryKey: ["tradeInPayoffs", locationId, selected?.cycleId!, selected?.payPeriodId!],
		queryFn: async ({ pageParam }) => {
			const cursor = pageParam as IPageParam<string>;
			const paginatedResponse = await getTradeInPayOffs(locationId ?? "", selected?.cycleId!, selected?.payPeriodId!, cursor.after);
			return paginatedResponse!;
		},
		enabled: and(!!selected?.cycleId, !!selected?.payPeriodId, !!locationId, isTabSelected, hasPermission),
		initialPageParam: DEFAULT_CURSOR,
		getNextPageParam: buildGetNextPageParam(PAGE_SIZE),
		fetchAllPages: true,
	});

	payoffsSubscription?.subscriptions?.onUpdateTradeInPayOffs?.setCallback(
		(response: GraphqlSubscriptionResponse<"onUpdateTradeInPayOffs", IPayoffSubscriptionResponse>) => {
			const { commissionCycleId, payPeriodId } = response.data?.onUpdateTradeInPayOffs ?? {};
			if (commissionCycleId !== selected?.cycleId || payPeriodId !== selected?.payPeriodId) {
				return;
			}
			queryClient.invalidateQueries({ queryKey: ["tradeInPayoffs", locationId, commissionCycleId, payPeriodId] });
		}
	);

	const renderContent = useCallback(() => {
		if (status === "pending") {
			return (
				<Grid item xs={12}>
					<TableLoader toolbar buttons={3} content rows={5} multiTitles={5}></TableLoader>
				</Grid>
			);
		}

		return tradeInPayoffs?.length ? (
			<TradeInPayoffsTable data={tradeInPayoffs} isFetching={isFetching} hasNextPage={hasNextPage} />
		) : (
			<EmptyBody
				image={emptyTable}
				title="No payoffs found for this period"
				bodyHeight="435px"
				sx={{ boxShadow: "none !important" }}
			/>
		);
	}, [status, tradeInPayoffs, isFetching]);

	return (
		<Box className="py-8">
			<Stack display="flex" direction="column">
				<Box height={626}>
					<Typography component="div" mb={1}>
						<Typography className="page-title" sx={{ lineHeight: "1.2" }}>
							{selected?.titleExtended} Payoffs
						</Typography>
					</Typography>
					<Stack display="flex" direction="column">
						{renderContent()}
					</Stack>
				</Box>
			</Stack>
		</Box>
	);
}
